import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CardStatus, DEFAULT_CATEGORY_ID } from '../../constants/app.constants';
import { AppStateService } from '../../core/app-state.service';
import { SessionVaultService } from '../../core/session-vault.service';
import { SelectorsService } from '../../core/selectors.service';
import { ErrorMessageComponent } from '../../shared/components/error-message/error-message.component';

@Component({
  selector: 'app-quick-create-card-dialog',
  imports: [ErrorMessageComponent, ReactiveFormsModule],
  template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm">
      <div
        class="w-full max-w-xl border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-slate-950/40"
        aria-modal="true"
        role="dialog"
        aria-labelledby="quick-create-title"
      >
        <h2 id="quick-create-title" class="mt-3 text-2xl font-semibold text-white">Create a new card</h2>
        <p class="mt-3 text-sm leading-6 text-white/70">
          Capture the title quickly, optionally assign a category, then continue in the full detail editor.
        </p>

        @if (feedbackMessages().length > 0) {
          <app-error-message class="mt-4" title="Create card" [messages]="feedbackMessages()" />
        }

        <form class="mt-6 space-y-4" [formGroup]="form" (ngSubmit)="createCard()">
          <div>
            <label class="mb-2 block text-sm font-medium text-white/80" for="quick-card-title">Title</label>
            <input
              id="quick-card-title"
              formControlName="title"
              class="w-full border border-white/10 bg-slate-950/70 px-4 py-3 text-white outline-none transition focus:border-cyan-300"
              placeholder="What needs to happen?"
              type="text"
            />
            @if (titleInvalid()) {
              <p class="mt-2 text-sm text-rose-200">Title is required.</p>
            }
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-white/80" for="quick-card-category">Category</label>
            <select
              id="quick-card-category"
              formControlName="categoryId"
              class="w-full border border-white/10 bg-slate-950/70 px-4 py-3 text-white outline-none transition focus:border-cyan-300"
            >
              @for (category of categories(); track category.id) {
                <option [value]="category.id">{{ category.name }}</option>
              }
            </select>
          </div>

          <div class="flex flex-wrap gap-3">
            <button
              class="bg-cyan-300 px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200"
              type="submit"
            >
              Create and open
            </button>
            <button
              class="border border-white/15 px-4 py-3 text-sm font-medium text-white/80 transition hover:border-white/30 hover:text-white"
              type="button"
              (click)="dismissed.emit()"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickCreateCardDialogComponent {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly appStateService = inject(AppStateService);
  private readonly sessionVaultService = inject(SessionVaultService);
  private readonly selectors = inject(SelectorsService);
  private readonly router = inject(Router);

  protected readonly dismissed = output<void>();
  protected readonly feedbackMessages = signal<string[]>([]);
  protected readonly categories = this.selectors.categories;
  protected readonly form = this.formBuilder.group({
    title: ['', [Validators.required]],
    categoryId: [DEFAULT_CATEGORY_ID, [Validators.required]],
  });

  protected titleInvalid(): boolean {
    const titleControl = this.form.controls.title;
    return titleControl.invalid && (titleControl.dirty || titleControl.touched);
  }

  protected async createCard(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.feedbackMessages.set(['Title is required before a card can be created.']);
      return;
    }

    if (!this.sessionVaultService.hasEncryptionPassword() && !(await this.sessionVaultService.requestEncryptionPassword(
      'Az uj kartya letrehozasa elott add meg az Encrypt jelszot.',
    ))) {
      return;
    }

    const { title, categoryId } = this.form.getRawValue();
    const result = this.appStateService.createCard({
      title,
      description: '',
      notes: '',
      categoryId,
      status: CardStatus.IN_PROGRESS,
      deadline: null,
      showInLadder: false,
      parentCardId: null,
      manualProgress: 0,
    });

    if (!result.isValid || !result.cardId) {
      this.feedbackMessages.set(result.errors.length > 0 ? result.errors : ['Failed to create card.']);
      return;
    }

    this.dismissed.emit();
    void this.router.navigate(['/cards', result.cardId]);
  }
}